<template>
  <div class="view-container">
    <div class="main-layout">
      <div class="left-panel">
        <div class="panel-header">
          <div class="header-right">
            <button class="btn-add-dir" @click="openAddDirDialog">
              <i class="fas fa-plus"></i> 新增目录
            </button>
          </div>
        </div>
        <div class="panel-body">
          <table class="dir-table">
            <thead>
              <tr>
                <th>流程目录</th>
                <th width="80">操作</th>
              </tr>
            </thead>
            <tbody>
              <!-- 根目录：全部流程 -->
              <tr 
                class="dir-row root-row"
                :class="{ selected: selectedDirId === '__root__' }"
                @click="selectDirRoot"
              >
                <td>
                  <span class="dir-name-cell">
                    <span class="expand-placeholder"></span>
                    <i class="fas fa-sitemap"></i>
                    <span class="dir-name">全部流程</span>
                    <span class="dir-count">({{ totalFlowCount }}个流程)</span>
                  </span>
                </td>
                <td class="action-cell"></td>
              </tr>
              <template v-for="(node, index) in processDirs" :key="node.id">
                <tr 
                  class="dir-row"
                  :class="{ selected: selectedDirId === node.id }"
                  @click="selectDir(node)"
                >
                  <td>
                    <span class="dir-name-cell">
                      <span v-if="node.children && node.children.length > 0" 
                            class="expand-icon"
                            :class="{ expanded: expandedDirIds.includes(node.id) }"
                            @click.stop="toggleDirExpand(node.id)">
                        <i class="fas fa-caret-right"></i>
                      </span>
                      <span v-else class="expand-placeholder"></span>
                      <i class="fas" :class="node.children && node.children.length > 0 ? 'fa-folder' : 'fa-folder-open'"></i>
                      <span class="dir-name">{{ node.name }}</span>
                      <span class="dir-count">({{ getProcessCount(node) }}个流程)</span>
                    </span>
                  </td>
                  <td class="action-cell">
                    <button class="action-btn edit" title="编辑" @click.stop="openEditDirDialog(node)">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="action-btn delete" title="删除" @click.stop="handleDeleteDir(node)">
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
                <template v-if="node.children && node.children.length > 0 && expandedDirIds.includes(node.id)">
                  <DirRow 
                    v-for="(child, childIdx) in node.children" 
                    :key="child.id"
                    :node="child"
                    :depth="1"
                    :selected-id="selectedDirId"
                    :expanded-ids="expandedDirIds"
                    @select="selectDir"
                    @edit="openEditDirDialog"
                    @delete="handleDeleteDir"
                    @toggle-expand="toggleDirExpand"
                  />
                </template>
              </template>
              <tr v-if="loadingDirs">
                <td colspan="2" class="empty-row">
                  <i class="fas fa-spinner fa-spin"></i> 加载目录中...
                </td>
              </tr>
              <tr v-else-if="dirError">
                <td colspan="2" class="empty-row error-row">
                  <span>{{ dirError }}</span>
                  <button class="btn-link" @click="loadCatalogs">重试</button>
                </td>
              </tr>
              <tr v-else-if="processDirs.length === 0">
                <td colspan="2" class="empty-row">
                  暂无流程目录，点击上方按钮新增
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="right-panel">
        <div class="panel-header">
          <div class="header-right">
            <button class="btn-add-process" @click="openAddProcessDialog">
              <i class="fas fa-plus"></i> 新增流程
            </button>
          </div>
        </div>
        <div class="panel-body">
          <div v-if="loadingProcesses" class="empty-state">
            <i class="fas fa-spinner fa-spin"></i>
            <p>加载流程中...</p>
          </div>
          <div v-else-if="processError" class="empty-state">
            <i class="fas fa-exclamation-circle" style="color: #f56c6c;"></i>
            <p>{{ processError }}</p>
            <button class="btn-link" @click="loadProcesses(selectedDirId)">重试</button>
          </div>
          <div v-else-if="selectedDirId && selectedDir">
            <div v-if="currentProcesses.length > 0" class="process-list">
              <div v-for="(process, idx) in currentProcesses" :key="process.id" class="process-card">
                <div class="process-card-main">
                  <div class="process-info">
                    <div class="process-name-row">
                      <span class="process-name">{{ process.name }}</span>
                      <span class="process-version">v{{ process.version }}</span>
                      <span class="valid-badge" :class="process.isValid ? 'valid' : 'invalid'">
                        <i class="fas" :class="process.isValid ? 'fa-check-circle' : 'fa-times-circle'"></i>
                        {{ process.isValid ? '有效' : '失效' }}
                      </span>
                    </div>
                    <div class="process-meta">
                      <span class="meta-item">
                        <i class="fas fa-calendar-alt"></i> {{ formatDate(process.createDate) }}
                      </span>
                      <span v-if="process.department" class="meta-item">
                        <i class="fas fa-building"></i> {{ process.department }}
                      </span>
                      <span v-if="process.state" class="meta-item">
                        <i class="fas fa-tag"></i> {{ process.state === 'active' ? '已发布' : '草稿' }}
                      </span>
                    </div>
                    <div class="process-desc">{{ process.description }}</div>
                  </div>
                  <div class="process-actions">
                    <div class="action-group">
                      <span class="action-group-label">设计</span>
                      <div class="action-group-btns">
                        <button class="p-action-btn primary" title="数据库设计" @click="handleDbDesign(process)">
                          <i class="fas fa-database"></i>
                          <span>数据库设计</span>
                        </button>
                        <button class="p-action-btn primary" title="流程设计" @click="handleProcessDesign(process)">
                          <i class="fas fa-sitemap"></i>
                          <span>流程设计</span>
                        </button>
                        <div class="p-dropdown-btn">
                          <button class="p-action-btn primary" title="表单设计" @click="handleFormDesign(process, 'new')">
                            <i class="fas fa-file-alt"></i>
                            <span>表单设计</span>
                          </button>
                          <button class="p-dropdown-arrow primary" @click.stop="formDesignMenu = formDesignMenu === process.guid ? '' : process.guid">&#9660;</button>
                          <div class="p-dropdown-menu" v-if="formDesignMenu === process.guid">
                            <div class="p-dropdown-item" @click="handleFormDesign(process, 'new'); formDesignMenu = ''"><i class="fas fa-cubes"></i> 新设计器</div>
                            <div class="p-dropdown-item" @click="handleFormDesign(process, 'old'); formDesignMenu = ''"><i class="fas fa-code"></i> 经典设计器</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="action-group">
                      <span class="action-group-label">维护</span>
                      <div class="action-group-btns">
                        <button class="p-action-btn" title="导入" @click="handleImport">
                          <i class="fas fa-upload"></i>
                        </button>
                        <button class="p-action-btn" title="导出" @click="handleExport(process)">
                          <i class="fas fa-download"></i>
                        </button>
                        <button class="p-action-btn" title="备份" @click="handleBackup(process)">
                          <i class="fas fa-copy"></i>
                        </button>
                        <button class="p-action-btn" title="还原" @click="handleRestore">
                          <i class="fas fa-undo"></i>
                        </button>
                        <button class="p-action-btn warning" title="流程修改" @click="openEditProcessDialog(process)">
                          <i class="fas fa-edit"></i>
                        </button>
                        <button v-if="process.isValid" class="p-action-btn danger" title="流程失效" @click="handleInvalidate(process)">
                          <i class="fas fa-ban"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="no-processes">
              <p>该目录暂无流程</p>
              <button @click="openAddProcessDialog" class="btn-add-process-sm">
                <i class="fas fa-plus"></i> 新增流程
              </button>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="fas fa-file-flow"></i>
            <p>请选择一个流程目录</p>
            <p class="hint">在左侧点击目录名称查看流程列表</p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDirModal" class="modal-overlay" @click.self="closeDirModal">
      <div class="modal-dialog">
        <div class="modal-header">
          <h4>{{ dirFormTitle }}</h4>
          <button @click="closeDirModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>目录名称 *</label>
            <input v-model="dirForm.name" type="text" class="form-control" placeholder="请输入目录名称" required>
          </div>
          <div class="form-group">
            <label>上级目录</label>
            <select v-model="dirForm.parentId" class="form-control">
              <option value="">无（顶级目录）</option>
              <option v-for="d in availableDirParents" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeDirModal" class="btn btn-default">取消</button>
          <button @click="saveDirForm" class="btn btn-primary">确认保存</button>
        </div>
      </div>
    </div>

    <div v-if="showProcessModal" class="modal-overlay" @click.self="closeProcessModal">
      <div class="modal-dialog process-dialog">
        <div class="modal-header">
          <h4>{{ processFormTitle }}</h4>
          <button @click="closeProcessModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group">
              <label>名称 *</label>
              <input v-model="processForm.name" type="text" class="form-control" placeholder="请输入流程名称">
            </div>
            <div class="form-group">
              <label>版本号</label>
              <input v-model="processForm.version" type="text" class="form-control" placeholder="如：1.0">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>序号</label>
              <input v-model="processForm.orderNo" type="number" class="form-control" placeholder="0">
            </div>
            <div class="form-group">
              <label>关联模式</label>
              <select v-model="processForm.relationMode" class="form-control">
                <option value="taskId">用户表的TaskID</option>
                <option value="other">其他模式</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>所属目录 *</label>
              <select v-model="processForm.dirId" class="form-control">
                <option value="">请选择目录</option>
                <option v-for="d in allDirOptions" :key="d.id" :value="d.id">{{ d.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>状态</label>
              <select v-model="processForm.state" class="form-control">
                <option value="draft">草稿</option>
                <option value="active">已发布</option>
              </select>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>生效日期</label>
              <input v-model="processForm.startDate" type="date" class="form-control">
            </div>
            <div class="form-group">
              <label>结束日期</label>
              <input v-model="processForm.endDate" type="date" class="form-control">
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>流水号设置</label>
              <div class="input-group">
                <input v-model="processForm.serialNoFormat" type="text" class="form-control" placeholder="流水号格式">
                <button class="btn btn-default" @click="selectSerialNoFormat">选择</button>
              </div>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>所属部门</label>
              <div class="input-group">
                <input v-model="processForm.department" type="text" class="form-control" placeholder="所属部门">
                <button class="btn btn-default" @click="selectDepartment">选择</button>
              </div>
            </div>
          </div>
          <div class="form-group">
            <label>流程描述</label>
            <textarea v-model="processForm.description" class="form-control" rows="3" placeholder="流程描述"></textarea>
          </div>
          <div class="form-group">
            <label>摘要信息</label>
            <div class="input-group">
              <input v-model="processForm.summary" type="text" class="form-control" placeholder="摘要信息">
              <button class="btn btn-default" @click="selectSummary">选择</button>
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>菜单图标</label>
              <div class="input-group">
                <input v-model="processForm.menuIcon" type="text" class="form-control" placeholder="菜单图标">
                <button class="btn btn-default" @click="selectMenuIcon">选择</button>
              </div>
            </div>
            <div class="form-group">
              <label>菜单图标</label>
              <div class="input-group">
                <input v-model="processForm.menuIcon2" type="text" class="form-control" placeholder="菜单图标">
                <button class="btn btn-default" @click="selectMenuIcon2">选择</button>
              </div>
            </div>
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input v-model="processForm.isValid" type="checkbox">
              <span>有效</span>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeProcessModal" class="btn btn-default">取消</button>
          <button @click="saveProcessForm" class="btn btn-primary">确认保存</button>
        </div>
      </div>
    </div>

    <!-- 数据库设计弹窗 -->
    <div v-if="showDbModal" class="modal-overlay" @click.self="closeDbModal">
      <div class="modal-dialog db-modal">
        <div class="modal-header">
          <h4><i class="fas fa-database"></i> 数据库设计 — {{ editingDbFlowName }}</h4>
          <button @click="closeDbModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="db-toolbar">
            <button class="btn btn-primary" @click="openCreateTableDialog"><i class="fas fa-plus"></i> 创建表</button>
            <button class="btn btn-primary" @click="openSelTableDialog"><i class="fas fa-search"></i> 新增现有表</button>
          </div>
          <div v-if="loadingDb" class="empty-row"><i class="fas fa-spinner fa-spin"></i> 加载中...</div>
          <table v-else-if="dbTables.length > 0" class="data-table">
            <thead>
              <tr>
                <th width="250">表名</th>
                <th width="80">表类型</th>
                <th>描述</th>
                <th width="80">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in dbTables" :key="item.guid || item.Guid">
                <td>{{ item.tableName || item.TableName }}</td>
                <td>{{ (item.tableType !== undefined ? item.tableType : item.TableType) === 0 ? '主表' : '子表' }}</td>
                <td>{{ item.tableRemark || item.TableRemark || '' }}</td>
                <td class="action-cell">
                  <button class="table-btn delete" @click="handleRemoveDbTable(item)"><i class="fas fa-trash"></i> 移除</button>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-else class="empty-row">暂无绑定数据表</div>
        </div>
        <div class="modal-footer">
          <button @click="closeDbModal" class="btn btn-default">关闭</button>
        </div>
      </div>
    </div>

    <!-- 创建表子弹窗 -->
    <div v-if="showCreateTableModal" class="modal-overlay" @click.self="closeCreateTableModal">
      <div class="modal-dialog create-table-dialog">
        <div class="modal-header">
          <h4><i class="fas fa-magic"></i> 创建表</h4>
          <button @click="closeCreateTableModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <div class="form-group" style="flex:1.5">
              <label>表名 *</label>
              <input v-model="createTableForm.tableName" class="form-control" placeholder="请输入表名（英文）" @input="onTableNameInput">
            </div>
            <div class="form-group" style="flex:0.8">
              <label>表类型 *</label>
              <select v-model="createTableForm.tableType" class="form-control">
                <option :value="0">主表</option>
                <option :value="1">子表</option>
              </select>
            </div>
            <div class="form-group" style="flex:1.2">
              <label>表描述</label>
              <input v-model="createTableForm.tableRemark" class="form-control" placeholder="表描述">
            </div>
          </div>
          <!-- 批量列生成 -->
          <div class="batch-columns">
            <div class="batch-header">
              <i class="fas fa-stream"></i> 快速生成列
              <span class="batch-hint">按前缀+流水号自动命名，支持跳过/中断编号</span>
            </div>
            <div class="batch-row">
              <div class="form-group" style="flex:0.4">
                <label>列名前缀</label>
                <input v-model="batchCol.prefix" class="form-control" placeholder="如 TA" style="text-transform:uppercase">
              </div>
              <div class="form-group" style="flex:0.3">
                <label>流水位数</label>
                <select v-model="batchCol.digits" class="form-control">
                  <option :value="2">2位(01-99)</option>
                  <option :value="3">3位(001-999)</option>
                  <option :value="4">4位(0001-9999)</option>
                </select>
              </div>
              <div class="form-group" style="flex:0.3">
                <label>起始序号</label>
                <input v-model.number="batchCol.startNo" type="number" min="1" class="form-control" placeholder="1">
              </div>
              <div class="form-group" style="flex:0.5">
                <label>生成数量</label>
                <input v-model.number="batchCol.count" type="number" min="1" max="50" class="form-control" placeholder="10">
              </div>
              <div class="form-group" style="flex:0.35">
                <label>&nbsp;</label>
                <button class="btn btn-primary btn-sm w-full" @click="batchGenerateColumns"><i class="fas fa-bolt"></i> 生成</button>
              </div>
            </div>
          </div>
          <!-- 列定义表格 -->
          <div class="columns-section">
            <div class="columns-header">
              <span><i class="fas fa-columns"></i> 列定义 ({{ createTableForm.columns.length }}列)</span>
              <button class="btn btn-default btn-sm" @click="addColumn"><i class="fas fa-plus"></i> 手动添加</button>
            </div>
            <div class="columns-table-wrap">
              <table class="data-table">
                <thead>
                  <tr>
                    <th width="36">序号</th>
                    <th width="30">主键</th>
                    <th width="140">列名</th>
                    <th width="150">类型</th>
                    <th width="90">长度/精度</th>
                    <th width="55">非空</th>
                    <th>说明</th>
                    <th width="50"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(col, idx) in createTableForm.columns" :key="idx" :class="{ 'key-row': col.isKey }">
                    <td class="col-seq">{{ idx + 1 }}</td>
                    <td><input type="checkbox" v-model="col.isKey" @change="onKeyChange(idx)"></td>
                    <td><input v-model="col.columnName" class="form-control form-control-sm" placeholder="列名" style="text-transform:uppercase"></td>
                    <td>
                      <select v-model="col.columnType" class="form-control form-control-sm" @change="onColumnTypeChange(idx)">
                        <optgroup label="-- 字符串 --">
                          <option v-for="ct in columnTypes.str" :key="ct.value" :value="ct.value">{{ ct.label }}</option>
                        </optgroup>
                        <optgroup label="-- 数值 --">
                          <option v-for="ct in columnTypes.num" :key="ct.value" :value="ct.value">{{ ct.label }}</option>
                        </optgroup>
                        <optgroup label="-- 日期/时间 --">
                          <option v-for="ct in columnTypes.date" :key="ct.value" :value="ct.value">{{ ct.label }}</option>
                        </optgroup>
                        <optgroup label="-- 其他 --">
                          <option v-for="ct in columnTypes.other" :key="ct.value" :value="ct.value">{{ ct.label }}</option>
                        </optgroup>
                      </select>
                    </td>
                    <td>
                      <div class="length-input-wrap" v-if="needLength(col.columnType)">
                        <input v-model="col.columnLength" class="form-control form-control-sm" :placeholder="lengthPlaceholder(col.columnType)">
                        <label v-if="needDecimal(col.columnType)" class="decimal-label">,小数{{ col.columnDecimal || '0' }}</label>
                        <input v-if="needDecimal(col.columnType)" v-model="col.columnDecimal" class="form-control form-control-sm decimal-input" placeholder="0">
                      </div>
                      <span v-else class="na-text">-</span>
                    </td>
                    <td><input type="checkbox" v-model="col.columnAllowEmpty" :disabled="col.isKey"></td>
                    <td><input v-model="col.columnRemark" class="form-control form-control-sm" placeholder="说明"></td>
                    <td>
                      <button v-if="createTableForm.columns.length > 1" class="btn-icon del" @click="removeColumn(idx)" title="移除此列"><i class="fas fa-times"></i></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="sql-preview" v-if="createTableSql">
            <div class="sql-preview-header">
              <label><i class="fas fa-code"></i> 生成 SQL</label>
              <span class="sql-hint">确认后将执行此 SQL 并自动绑定到当前流程</span>
            </div>
            <textarea :value="createTableSql" class="form-control sql-textarea" rows="6" readonly></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeCreateTableModal" class="btn btn-default">取消</button>
          <button @click="doCreateTable" class="btn btn-primary"><i class="fas fa-check"></i> 确认创建并绑定</button>
        </div>
      </div>
    </div>

    <!-- 新增现有表子弹窗 -->
    <div v-if="showSelTableModal" class="modal-overlay" @click.self="closeSelTableModal">
      <div class="modal-dialog sel-table-dialog">
        <div class="modal-header">
          <h4><i class="fas fa-search"></i> 新增现有表</h4>
          <button @click="closeSelTableModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="sel-search">
            <i class="fas fa-filter"></i>
            <input v-model="selSearch" class="form-control" placeholder="搜索表名或描述...">
            <span class="sel-count">{{ filteredDbTables.length }} / {{ allDbTables.length }} 项</span>
          </div>
          <div class="sel-table-list">
            <div 
              v-for="t in filteredDbTables" 
              :key="t.tableName" 
              class="sel-table-item" 
              :class="{ selected: selTableForm.tableName === t.tableName }"
              @click="selTableForm.tableName = t.tableName"
            >
              <span class="sel-type-icon">
                <i :class="t.tableTypeName === '视图' ? 'fas fa-eye' : 'fas fa-table'"></i>
              </span>
              <span class="sel-name">{{ t.tableName }}</span>
              <span class="sel-badge" :class="t.tableTypeName === '视图' ? 'view' : 'table'">{{ t.tableTypeName }}</span>
              <span v-if="t.description" class="sel-desc">{{ t.description }}</span>
            </div>
            <div v-if="filteredDbTables.length === 0" class="sel-empty">无匹配的表/视图</div>
          </div>
          <div class="form-row" style="margin-top:12px">
            <div class="form-group">
              <label>表类型 *</label>
              <select v-model="selTableForm.tableType" class="form-control">
                <option :value="0">主表</option>
                <option :value="1">子表</option>
              </select>
            </div>
            <div class="form-group">
              <label>描述</label>
              <input v-model="selTableForm.tableRemark" class="form-control" placeholder="自定义描述（可选）">
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeSelTableModal" class="btn btn-default">取消</button>
          <button @click="doAddExistingTable" class="btn btn-primary" :disabled="!selTableForm.tableName"><i class="fas fa-check"></i> 确认添加</button>
        </div>
      </div>
    </div>

    <!-- ===== 流水号选择器 (Tabs: 系统变量/表单字段/流程发起人/流程变量/日期格式/自增) ===== -->
    <div v-if="showSerialSelector" class="modal-overlay" @click.self="closeSerialSelector">
      <div class="modal-dialog" style="width:580px">
        <div class="modal-header">
          <h4><i class="fas fa-barcode"></i> 流水号</h4>
          <button @click="closeSerialSelector" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Tabs -->
          <div class="selector-tabs">
            <div v-for="(tab, idx) in serialTabs" :key="idx"
                 class="selector-tab" :class="{ active: serialActiveTab === idx }"
                 @click="serialActiveTab = idx">
              {{ tab }}
            </div>
          </div>
          <div class="selector-tab-content">
            <!-- 系统变量 -->
            <template v-if="serialActiveTab === 0">
              <div class="selector-tree-list">
                <div v-for="item in serialSysVars" :key="item" class="selector-tree-item" @dblclick="onSerialInsertToCursor(item)">
                  <i class="fas fa-cube" style="color:#4a90d9;margin-right:6px"></i>{{ item }}
                </div>
              </div>
            </template>
            <!-- 表单字段 -->
            <template v-if="serialActiveTab === 1">
              <div v-if="serialFormFields.length === 0" class="selector-empty">请先选择数据库表</div>
              <div v-else class="selector-tree-list">
                <div v-for="f in serialFormFields" :key="f" class="selector-tree-item" @dblclick="onSerialInsertToCursor(f)">
                  <i class="fas fa-cube" style="color:#e67e22;margin-right:6px"></i>{{ f }}
                </div>
              </div>
            </template>
            <!-- 流程发起人 -->
            <template v-if="serialActiveTab === 2">
              <div class="selector-tree-list">
                <div v-for="item in serialInitiatorFields" :key="item" class="selector-tree-item" @dblclick="onSerialInsertToCursor('Initiator.' + item)">
                  <i class="fas fa-user" style="color:#27ae60;margin-right:6px"></i>{{ 'Initiator.' + item }}
                </div>
              </div>
            </template>
            <!-- 流程变量 -->
            <template v-if="serialActiveTab === 3">
              <div class="selector-empty">暂无流程变量</div>
            </template>
            <!-- 日期格式 -->
            <template v-if="serialActiveTab === 4">
              <div class="selector-tree-list">
                <div v-for="item in serialDateFormats" :key="item.value" class="selector-tree-item" @dblclick="onSerialInsertToCursor(item.value)">
                  <i class="fas fa-calendar" style="color:#8e44ad;margin-right:6px"></i>{{ item.label }}
                </div>
              </div>
            </template>
            <!-- 自增 -->
            <template v-if="serialActiveTab === 5">
              <div class="serial-sn-form">
                <div class="serial-sn-row">
                  <label>初始值：</label>
                  <input v-model.number="serialSn.initial" type="number" class="form-control" min="0" style="width:120px">
                </div>
                <div class="serial-sn-row">
                  <label>长度：</label>
                  <input v-model.number="serialSn.length" type="number" class="form-control" min="1" max="20" style="width:120px">
                </div>
                <div class="serial-sn-row">
                  <label>增量：</label>
                  <input v-model.number="serialSn.increment" type="number" class="form-control" min="1" style="width:120px">
                </div>
                <button class="btn btn-primary" @click="onSerialSnInsert" style="margin-top:8px">
                  <i class="fas fa-check"></i> 插入
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== 部门选择器 (树形结构) ===== -->
    <div v-if="showDeptSelector" class="modal-overlay" @click.self="closeDeptSelector">
      <div class="modal-dialog" style="width:520px">
        <div class="modal-header">
          <h4><i class="fas fa-building"></i> 选择部门</h4>
          <button @click="closeDeptSelector" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="dept-tree-container">
            <div v-if="deptTreeLoading" class="selector-empty"><i class="fas fa-spinner fa-spin"></i> 加载中...</div>
            <div v-else-if="deptTree.length === 0" class="selector-empty">暂无部门数据</div>
            <template v-else>
              <div v-for="node in deptTree" :key="node.id" class="dept-tree-node">
                <div class="dept-tree-row" :class="{ 'dept-selected': deptSelectedId === node.id }" @click="deptSelectedId = node.id">
                  <span class="dept-tree-indent" :style="{ width: (node._level * 20) + 'px' }"></span>
                  <i class="fas fa-chevron-right dept-tree-arrow" :class="{ expanded: node._expanded }" v-if="node._hasChildren" @click.stop="toggleDeptNode(node)"></i>
                  <span v-else class="dept-tree-arrow-placeholder"></span>
                  <i class="fas fa-building dept-tree-icon"></i>
                  <span class="dept-tree-label">{{ node.name }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeDeptSelector" class="btn btn-default">取消</button>
          <button @click="confirmDeptSelect" class="btn btn-primary" :disabled="!deptSelectedId">
            <i class="fas fa-check"></i> 选择
          </button>
        </div>
      </div>
    </div>

    <!-- ===== 摘要信息选择器 (Tabs: 当前步拥有人/代理人/流程发起人/日期星期/表单字段/系统变量) ===== -->
    <div v-if="showSummarySelector" class="modal-overlay" @click.self="closeSummarySelector">
      <div class="modal-dialog" style="width:580px">
        <div class="modal-header">
          <h4><i class="fas fa-list-alt"></i> 摘要内容</h4>
          <button @click="closeSummarySelector" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="selector-tabs">
            <div v-for="(tab, idx) in summaryTabs" :key="idx"
                 class="selector-tab" :class="{ active: summaryActiveTab === idx }"
                 @click="summaryActiveTab = idx">
              {{ tab }}
            </div>
          </div>
          <div class="selector-tab-content">
            <template v-if="summaryActiveTab === 0">
              <div class="selector-tree-list">
                <div v-for="item in summaryLoginUserFields" :key="item" class="selector-tree-item" @dblclick="onSummaryAppend('LoginUser.' + item)">
                  <i class="fas fa-user" style="color:#4a90d9;margin-right:6px"></i>{{ 'LoginUser.' + item }}
                </div>
              </div>
            </template>
            <template v-if="summaryActiveTab === 1">
              <div class="selector-tree-list">
                <div v-for="item in summaryAgentFields" :key="item" class="selector-tree-item" @dblclick="onSummaryAppend('AgentUser.' + item)">
                  <i class="fas fa-user-friends" style="color:#e67e22;margin-right:6px"></i>{{ 'AgentUser.' + item }}
                </div>
              </div>
            </template>
            <template v-if="summaryActiveTab === 2">
              <div class="selector-tree-list">
                <div v-for="item in summaryInitiatorFields" :key="item" class="selector-tree-item" @dblclick="onSummaryAppend('Initiator.' + item)">
                  <i class="fas fa-user-plus" style="color:#27ae60;margin-right:6px"></i>{{ 'Initiator.' + item }}
                </div>
              </div>
            </template>
            <template v-if="summaryActiveTab === 3">
              <div class="selector-tree-list">
                <div v-for="item in summaryDateFields" :key="item.value" class="selector-tree-item" @dblclick="onSummaryAppend(item.value)">
                  <i class="fas fa-calendar" style="color:#8e44ad;margin-right:6px"></i>{{ item.label }}
                </div>
              </div>
            </template>
            <template v-if="summaryActiveTab === 4">
              <div v-if="summaryFormFields.length === 0" class="selector-empty">请先选择数据库表</div>
              <div v-else class="selector-tree-list">
                <div v-for="f in summaryFormFields" :key="f" class="selector-tree-item" @dblclick="onSummaryAppend(f)">
                  <i class="fas fa-cube" style="color:#e67e22;margin-right:6px"></i>{{ f }}
                </div>
              </div>
            </template>
            <template v-if="summaryActiveTab === 5">
              <div class="selector-tree-list">
                <div v-for="item in summarySysVars" :key="item" class="selector-tree-item" @dblclick="onSummaryAppend(item)">
                  <i class="fas fa-cube" style="color:#4a90d9;margin-right:6px"></i>{{ item }}
                </div>
              </div>
            </template>
          </div>
          <div class="summary-current">
            <span class="preview-label">当前：</span>
            <code>{{ processForm.summary || '未设置' }}</code>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeSummarySelector" class="btn btn-default">取消</button>
        </div>
      </div>
    </div>

    <!-- ===== 图标选择器 (图片网格 + 分页) ===== -->
    <div v-if="showIconSelector" class="modal-overlay" @click.self="closeIconSelector">
      <div class="modal-dialog" style="width:580px">
        <div class="modal-header">
          <h4><i class="fas fa-icons"></i> {{ iconTarget === 'menuIcon' ? '菜单图标' : '菜单图标2' }}</h4>
          <button @click="closeIconSelector" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div v-if="iconLoading" class="selector-empty"><i class="fas fa-spinner fa-spin"></i> 加载中...</div>
          <div v-else-if="iconList.length === 0" class="selector-empty">暂无图标文件，请将图标放到 wwwroot/icons 目录</div>
          <div v-else>
            <div class="icon-grid">
              <div v-for="icon in iconList" :key="icon.name" class="icon-item" :class="{ active: selectedIconName === icon.name }" @click="selectedIconName = icon.name">
                <img :src="icon.url" :alt="icon.name" class="icon-img" />
                <span class="icon-name">{{ icon.name }}</span>
              </div>
            </div>
            <div class="icon-pagination" v-if="iconTotal > iconPageSize">
              <button class="btn-page" :disabled="iconPage <= 1" @click="loadIcons(iconPage - 1)">
                <i class="fas fa-angle-left"></i>
              </button>
              <span class="page-info">第 {{ iconPage }} 页 共 {{ Math.ceil(iconTotal / iconPageSize) }} 页</span>
              <span class="page-total">显示 {{ (iconPage-1)*iconPageSize+1 }}-{{ Math.min(iconPage*iconPageSize, iconTotal) }}条，共 {{ iconTotal }} 条</span>
              <button class="btn-page" :disabled="iconPage >= Math.ceil(iconTotal / iconPageSize)" @click="loadIcons(iconPage + 1)">
                <i class="fas fa-angle-right"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeIconSelector" class="btn btn-default">取消</button>
          <button @click="confirmIconSelect" class="btn btn-primary" :disabled="!selectedIconName">
            <i class="fas fa-check"></i> 保存图片
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineComponent, h, onMounted, watch } from 'vue'
import { getCatalogs, saveCatalog, deleteCatalog, getProcessList, getProcess, saveProcess, deleteProcess, backupProcess, exportProcess, getDbTables, saveDbTable, deleteDbTable, getDatabaseTables, createTable, getIcons } from '../api/process'
import { getOrgTree } from '../api/org'
import { getTableColumns } from '../api/process'

// ===== 数据状态 =====
const processDirs = ref([])      // 目录树（原始数组）
const allCatalogs = ref({})      // { id: node } 快速索引，用于流程计数
const processes = ref([])        // 流程列表
const totalFlowCount = ref(0)    // 全部流程总数

const loadingDirs = ref(false)
const loadingProcesses = ref(false)
const dirError = ref('')
const processError = ref('')
const formDesignMenu = ref('')     // 表单设计下拉菜单当前展开的流程guid
const previewProcess = ref(null)   // 预览中的流程

const selectedDirId = ref(null)
const expandedDirIds = ref([])
const showDirModal = ref(false)
const showProcessModal = ref(false)
const isDirEdit = ref(false)
const isProcessEdit = ref(false)
const editingDirId = ref(null)
const editingProcessGuid = ref(null)

// 数据库设计弹窗
const showDbModal = ref(false)
const showCreateTableModal = ref(false)
const showSelTableModal = ref(false)
const editingDbFlowGuid = ref('')
const editingDbFlowName = ref('')
const dbTables = ref([])
const loadingDb = ref(false)
const allDbTables = ref([])  // 数据库全部表/视图
const selTableForm = ref({ tableName: '', tableType: 0, tableRemark: '' })
const selSearch = ref('')
const createTableForm = ref({
  tableName: '', tableType: 0, tableRemark: '',
  columns: [{ columnName: 'ID', columnType: 'int', columnLength: '', columnDecimal: '', columnAllowEmpty: false, columnRemark: '主键ID', isKey: true }]
})
const batchCol = ref({ prefix: 'TA', digits: 3, startNo: 1, count: 10 })
const columnTypes = {
  str: [
    { value: 'char', label: 'char' },
    { value: 'nchar', label: 'nchar' },
    { value: 'varchar', label: 'varchar' },
    { value: 'nvarchar', label: 'nvarchar' },
    { value: 'text', label: 'text' },
    { value: 'ntext', label: 'ntext' }
  ],
  num: [
    { value: 'bit', label: 'bit' },
    { value: 'tinyint', label: 'tinyint' },
    { value: 'smallint', label: 'smallint' },
    { value: 'int', label: 'int' },
    { value: 'bigint', label: 'bigint' },
    { value: 'decimal', label: 'decimal' },
    { value: 'numeric', label: 'numeric' },
    { value: 'float', label: 'float' },
    { value: 'real', label: 'real' },
    { value: 'money', label: 'money' },
    { value: 'smallmoney', label: 'smallmoney' }
  ],
  date: [
    { value: 'datetime', label: 'datetime' },
    { value: 'datetime2', label: 'datetime2' },
    { value: 'smalldatetime', label: 'smalldatetime' },
    { value: 'date', label: 'date' },
    { value: 'time', label: 'time' }
  ],
  other: [
    { value: 'uniqueidentifier', label: 'uniqueidentifier' },
    { value: 'xml', label: 'xml' },
    { value: 'varbinary', label: 'varbinary' },
    { value: 'image', label: 'image' }
  ]
}

const dirForm = ref({ name: '', parentId: '' })
const processForm = ref({
  name: '', version: '1.0', orderNo: 0, relationMode: 'taskId',
  dirId: '', state: 'draft', startDate: '', endDate: '',
  serialNoFormat: '', department: '', description: '', summary: '',
  menuIcon: '', menuIcon2: '', isValid: true
})

// ===== API 数据转换 =====
// 将后端目录数据转为前端树形格式（ASP.NET Core 默认 camelCase 序列化，优先取小写属性）
function mapCatalogTree(rawList) {
  if (!rawList || !rawList.length) return []
  function convert(node) {
    const id = node.id || node.Id || node.Guid
    const name = node.text || node.Text || node.enName || node.EnName || node.name
    const flowCount = node.flowCount !== undefined ? node.flowCount : (node.FlowCount || 0)
    const children = node.childFolder || node.ChildFolder || node.children || []
    return {
      id,
      name,
      flowCount,
      parentId: node.superiorGuid || node.SuperiorGuid || '',
      children: Array.isArray(children) ? children.map(convert) : []
    }
  }
  return rawList.map(convert)
}

// 将后端流程数据转为前端格式（ASP.NET Core 默认 camelCase 序列化，优先取小写属性）
function mapProcess(raw) {
  return {
    id: raw.guid || raw.Guid || raw.id,
    guid: raw.guid || raw.Guid || raw.id,
    name: raw.processName || raw.ProcessName || raw.name,
    description: raw.remark || raw.Remark || raw.description || '',
    version: raw.vesion || raw.Vesion || raw.version || '1.0',
    dirId: raw.catalogGuid || raw.CatalogGuid || raw.dirId || '',
    createDate: (raw.createTime || raw.CreateTime) ? (raw.createTime || raw.CreateTime || '').split('T')[0] : (raw.createDate || ''),
    isValid: (raw.active !== undefined ? raw.active : raw.Active) !== 0,
    orderNo: raw.no || raw.No || raw.orderNo || 0,
    relationMode: raw.para4 || raw.Para4 || raw.relationMode || 'taskId',
    state: (raw.states !== undefined ? (raw.states === 0 ? 'draft' : 'active') : (raw.States !== undefined ? (raw.States === 0 ? 'draft' : 'active') : raw.state)) || 'draft',
    startDate: (raw.startTime || raw.StartTime) ? (raw.startTime || raw.StartTime || '').split('T')[0] : (raw.startDate || ''),
    endDate: (raw.endTime || raw.EndTime) ? (raw.endTime || raw.EndTime || '').split('T')[0] : (raw.endDate || ''),
    serialNoFormat: raw.serialNum || raw.SerialNum || raw.serialNoFormat || '',
    department: raw.orgsGuid || raw.OrgsGuid || raw.department || '',
    summary: raw.summaryInfo || raw.SummaryInfo || raw.summary || '',
    menuIcon: raw.para1 || raw.Para1 || raw.menuIcon || '',
    menuIcon2: raw.para3 || raw.Para3 || raw.menuIcon2 || '',
    onwerAccount: raw.onwerAccount || raw.OnwerAccount || '',
    onwerName: raw.onwerName || raw.OnwerName || '',
    para2: raw.para2 || raw.Para2 || ''
  }
}

// ===== 计算属性 =====
const dirFormTitle = computed(() => isDirEdit.value ? '编辑目录' : '新增目录')
const processFormTitle = computed(() => isProcessEdit.value ? '编辑流程' : '新增流程')

const selectedDir = computed(() => {
  if (selectedDirId.value === '__root__') {
    return { id: '__root__', name: '全部流程' }
  }
  function findNode(nodes, id) {
    for (const n of nodes) {
      if (n.id === id) return n
      if (n.children) { const f = findNode(n.children, id); if (f) return f }
    }
    return null
  }
  return findNode(processDirs.value, selectedDirId.value)
})

const currentProcesses = computed(() =>
  processes.value.filter(p => p.dirId === selectedDirId.value)
)

const allDirOptions = computed(() => {
  const result = []
  function walk(nodes) {
    for (const n of nodes) {
      result.push({ id: n.id, name: n.name })
      if (n.children) walk(n.children)
    }
  }
  walk(processDirs.value)
  return result
})

const availableDirParents = computed(() => {
  const result = []
  const excludeId = isDirEdit.value ? editingDirId.value : null
  function walk(nodes) {
    for (const n of nodes) {
      if (n.id !== excludeId) result.push({ id: n.id, name: n.name })
      if (n.children) walk(n.children)
    }
  }
  walk(processDirs.value)
  return result
})

// ===== 数据加载 =====
async function loadCatalogs() {
  loadingDirs.value = true
  dirError.value = ''
  try {
    const res = await getCatalogs()
    const data = res.Data || res.data || res
    processDirs.value = mapCatalogTree(Array.isArray(data) ? data : [])
    // 建立快速索引 & 计算总数
    const idx = {}
    let total = 0
    function walk(nodes) {
      nodes.forEach(n => {
        idx[n.id] = n
        total += n.flowCount
        if (n.children) walk(n.children)
      })
    }
    walk(processDirs.value)
    allCatalogs.value = idx
    totalFlowCount.value = total
    // 默认选中全部流程
    if (!selectedDirId.value) {
      selectedDirId.value = '__root__'
    }
  } catch (e) {
    console.error('加载目录失败', e)
    dirError.value = e.message || '加载目录失败'
  } finally {
    loadingDirs.value = false
  }
}

async function loadProcesses(catalogGuid) {
  if (!catalogGuid) return
  loadingProcesses.value = true
  processError.value = ''
  try {
    const res = await getProcessList(catalogGuid)
    const data = res.Data || res.data || res
    const list = Array.isArray(data) ? data : []
    // 适配不同后端返回格式: 可能是分页对象 { rows: [...] } 或直接数组
    const rows = list.rows || list
    processes.value = (Array.isArray(rows) ? rows : []).map(mapProcess)
  } catch (e) {
    console.error('加载流程失败', e)
    processError.value = e.message || '加载流程失败'
    processes.value = []
  } finally {
    loadingProcesses.value = false
  }
}

// 选中目录时加载流程（'__root__' → 加载所有流程）
watch(selectedDirId, (newId) => {
  if (newId !== null && newId !== undefined) {
    loadProcesses(newId === '__root__' ? '' : newId)
  }
})

onMounted(() => {
  loadCatalogs()
})

// ===== 目录操作 =====
function selectDir(node) { selectedDirId.value = node.id }
function selectDirRoot() { selectedDirId.value = '__root__' }
function toggleDirExpand(id) {
  const idx = expandedDirIds.value.indexOf(id)
  if (idx > -1) expandedDirIds.value.splice(idx, 1)
  else expandedDirIds.value.push(id)
}
function getProcessCount(node) {
  return node.flowCount || 0
}
function formatDate(d) { if (!d) return ''; return d }

function openAddDirDialog() {
  isDirEdit.value = false
  dirForm.value = { name: '', parentId: selectedDirId.value || '' }
  showDirModal.value = true
}
function openEditDirDialog(node) {
  isDirEdit.value = true
  editingDirId.value = node.id
  dirForm.value = { name: node.name, parentId: node.parentId || '' }
  showDirModal.value = true
}
function closeDirModal() { showDirModal.value = false }

async function saveDirForm() {
  if (!dirForm.value.name.trim()) { $message.warning('请输入目录名称'); return }
  try {
    const payload = {
      Guid: isDirEdit.value ? editingDirId.value : undefined,
      EnName: dirForm.value.name,
      SuperiorGuid: dirForm.value.parentId || '',
      Type: 1  // 流程目录类型
    }
    await saveCatalog(payload)
    closeDirModal()
    await loadCatalogs()
  } catch (e) {
    $message.error('保存失败: ' + (e.message || e))
  }
}

async function handleDeleteDir(node) {
  if (!await $confirm('确定删除此目录？')) return
  try {
    await deleteCatalog(node.id)
    if (selectedDirId.value === node.id) selectedDirId.value = null
    await loadCatalogs()
  } catch (e) {
    $message.error('删除失败: ' + (e.message || e))
  }
}

function findNodeById(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n
    if (n.children) { const f = findNodeById(n.children, id); if (f) return f }
  }
  return null
}

// ===== 流程操作 =====
function openAddProcessDialog() {
  isProcessEdit.value = false
  processForm.value = {
    name: '', version: '1.0', orderNo: 0, relationMode: 'taskId',
    dirId: selectedDirId.value || '', state: 'draft', startDate: '', endDate: '',
    serialNoFormat: '', department: '', description: '', summary: '',
    menuIcon: '', menuIcon2: '', isValid: true
  }
  showProcessModal.value = true
}
function openEditProcessDialog(p) {
  isProcessEdit.value = true
  editingProcessGuid.value = p.guid || p.id
  processForm.value = { ...p }
  showProcessModal.value = true
}
function closeProcessModal() { showProcessModal.value = false }

async function saveProcessForm() {
  if (!processForm.value.name.trim()) { $message.warning('请输入流程名称'); return }
  try {
    const payload = {
      Type: isProcessEdit.value ? 'Edit' : 'Add',
      Guid: isProcessEdit.value ? editingProcessGuid.value : undefined,
      ProcessName: processForm.value.name,
      No: processForm.value.orderNo,
      CatalogGuid: processForm.value.dirId,
      Vesion: processForm.value.version,
      Remark: processForm.value.description,
      SerialNo: processForm.value.serialNoFormat,
      SummaryInfo: processForm.value.summary,
      Para1: processForm.value.menuIcon,
      Para3: processForm.value.menuIcon2,
      Para4: processForm.value.relationMode,
      OrgsGuid: processForm.value.department,
      States: processForm.value.state === 'active' ? 1 : 0,
      Active: processForm.value.isValid ? 1 : 0,
      StartTime: processForm.value.startDate || '1900-01-01',
      EndTime: processForm.value.endDate || '2099-12-31'
    }
    await saveProcess(payload)
    closeProcessModal()
    await loadProcesses(selectedDirId.value)
    await loadCatalogs() // 刷新目录流程计数
  } catch (e) {
    $message.error('保存失败: ' + (e.message || e))
  }
}

async function handleInvalidate(process) {
  if (!await $confirm('确定使该流程失效？')) return
  try {
    // 传递完整数据，避免后端 UPDATE 覆盖其他字段
    await saveProcess({
      Type: 'Edit',
      Guid: process.guid || process.id,
      ProcessName: process.name,
      No: process.orderNo,
      CatalogGuid: process.dirId,
      Vesion: process.version,
      Remark: process.description,
      SerialNo: process.serialNoFormat,
      SummaryInfo: process.summary,
      Para1: process.menuIcon,
      Para3: process.menuIcon2,
      Para4: process.relationMode,
      OrgsGuid: process.department,
      States: process.state === 'active' ? 1 : 0,
      Active: 0,
      StartTime: process.startDate || '1900-01-01',
      EndTime: process.endDate || '2099-12-31'
    })
    await loadProcesses(selectedDirId.value)
    await loadCatalogs()
  } catch (e) {
    $message.error('操作失败: ' + (e.message || e))
  }
}

async function handleDeleteProcess(process) {
  if (!await $confirm('确定删除该流程及其所有历史版本记录？')) return
  try {
    await deleteProcess(process.guid || process.id)
    await loadProcesses(selectedDirId.value)
    await loadCatalogs()
  } catch (e) {
    $message.error('删除失败: ' + (e.message || e))
  }
}

// ===== 设计器跳转（参考 WorkFlowMgr.aspx） =====
const BASE_URL = '/CloudWeb/Modules/DevelopManages/WorkFlow'

function handleDbDesign(process) {
  editingDbFlowGuid.value = process.guid || process.id
  editingDbFlowName.value = process.name || ''
  showDbModal.value = true
  loadDbTables()
}

function handleProcessDesign(process) {
  // 流程设计 → 内嵌 SPA Tab（ProcessDesigner）
  window.currentEditingProcess = {
    guid: process.guid || process.id,
    name: process.name || '',
    version: process.version || '1.0',
    catalogGuid: selectedDirId.value || ''
  }
  window.dispatchEvent(new CustomEvent('navigateToProcessDesigner'))
}

function handleFormDesign(process, mode) {
  // 表单设计
  window.currentEditingProcess = {
    guid: process.guid || process.id,
    name: process.name || '',
    version: process.version || '1.0',
    catalogGuid: selectedDirId.value || ''
  }
  if (mode === 'old') {
    window.dispatchEvent(new CustomEvent('navigateToOldFormDesigner'))
  } else {
    window.dispatchEvent(new CustomEvent('navigateToFormDesigner'))
  }
}

function handleHistory(process) {
  // 历史流程
  const hisFlowGuid = process.para2 || process.id
  const url = `${BASE_URL}/ProcessHistory.aspx?hisFlowGuid=${encodeURIComponent(hisFlowGuid)}`
  window.open(url, '_blank', `width=800,height=500,resizable=yes,scrollbars=yes`)
}

async function handleImport() {
  const catalogGuid = selectedDirId.value || ''
  const url = `${BASE_URL}/ImportFlow.html?catalogGuid=${encodeURIComponent(catalogGuid)}`
  window.open(url, '_blank', `width=600,height=400,resizable=yes,scrollbars=yes`)
}

async function handleExport(process) {
  if (!await $confirm('确认导出该流程？')) return
  try {
    await exportProcess({
      flowGuid: process.guid || process.id,
      flowname: process.name,
      vesion: process.version,
      sysflowGuid: process.para2 || process.id
    })
  } catch (e) {
    $message.error('导出失败: ' + (e.message || e))
  }
}

async function handleBackup(process) {
  if (!await $confirm('确认备份该流程？')) return
  try {
    await backupProcess({
      flowGuid: process.guid || process.id,
      flowname: process.name,
      vesion: process.version,
      sysflowGuid: process.para2 || process.id
    })
  } catch (e) {
    $message.error('备份失败: ' + (e.message || e))
  }
}

function handleRestore() {
  const catalogGuid = selectedDirId.value || ''
  const url = `${BASE_URL}/FlowBackupIn.aspx?catalogGuid=${encodeURIComponent(catalogGuid)}`
  window.open(url, '_blank', `width=700,height=500,resizable=yes,scrollbars=yes`)
}

// ===== 选择器弹窗 =====
const showSerialSelector = ref(false)
const showDeptSelector = ref(false)
const showSummarySelector = ref(false)
const showIconSelector = ref(false)

// --- 流水号选择器 ---
const serialActiveTab = ref(0)
const serialTabs = ['系统变量', '表单字段', '流程发起人', '流程变量', '日期格式', '自增']
const serialSysVars = ['TaskId', 'FlowGuid', 'FlowName']
const serialInitiatorFields = ['Account','Birthday','CostCenter','JoinDate','Description','DisplayName','Email','FaminlyTel','HRID','Mb','Office','Tel','Sex','Job','Levels','QQ']
const serialDateFormats = [
  { label: '年-月-日(YYYY-MM-DD)', value: 'YYYY-MM-DD' },
  { label: '年月日(YYYYMMDD)', value: 'YYYYMMDD' },
  { label: '年月日(YYMMDD)', value: 'YYMMDD' },
  { label: '年-月(YYYY-MM)', value: 'YYYY-MM' },
  { label: '年月(YYYYMM)', value: 'YYYYMM' },
  { label: '年(YYYY)', value: 'YYYY' }
]
const serialSn = ref({ initial: 1, length: 4, increment: 1 })
const serialFormFields = ref([])

function selectSerialNoFormat() {
  serialActiveTab.value = 0
  serialFormFields.value = []
  // 如果有数据库表，加载字段
  if (editingProcessGuid.value) {
    loadSerialFormFields()
  }
  showSerialSelector.value = true
}
async function loadSerialFormFields() {
  // 尝试从当前流程的数据库表加载字段
  try {
    const tables = await getDbTables(editingProcessGuid.value)
    const data = tables?.Data || tables?.data || []
    if (Array.isArray(data) && data.length > 0) {
      const tableName = data[0].tableName || data[0].TableName
      const cols = await getTableColumns(tableName)
      const colData = cols?.Data || cols?.data || []
      serialFormFields.value = Array.isArray(colData) ? colData.map(c => c.columnName || c.ColumnName) : []
    }
  } catch (e) { /* ignore */ }
}
function onSerialInsertToCursor(value) {
  const input = document.querySelector('.process-dialog input[placeholder="流水号格式"]')
  if (input) {
    const start = input.selectionStart ?? 0
    const end = input.selectionEnd ?? 0
    const text = processForm.value.serialNoFormat || ''
    processForm.value.serialNoFormat = text.substring(0, start) + value + text.substring(end)
    input.focus()
    input.setSelectionRange(start + value.length, start + value.length)
  } else {
    processForm.value.serialNoFormat = (processForm.value.serialNoFormat || '') + value
  }
}
function onSerialSnInsert() {
  const { initial, length } = serialSn.value
  const padding = String(initial).padStart(length, '0')
  // 生成一个自增标记，后端会根据规则生成实际值
  onSerialInsertToCursor('{' + padding + '}')
}
function closeSerialSelector() { showSerialSelector.value = false }

// --- 部门选择器 ---
const deptTree = ref([])
const deptTreeLoading = ref(false)
const deptSelectedId = ref('')

async function selectDepartment() {
  deptTreeLoading.value = true
  deptSelectedId.value = processForm.value.department || ''
  try {
    const res = await getOrgTree()
    const data = res?.Data || res?.data || res
    deptTree.value = flattenOrgTree(Array.isArray(data) ? data : [])
  } catch (e) {
    console.error('加载部门失败', e)
    deptTree.value = []
  } finally {
    deptTreeLoading.value = false
  }
  showDeptSelector.value = true
}

function flattenOrgTree(nodes, level = 0) {
  const result = []
  for (const n of nodes) {
    const id = n.id || n.Id || n.Guid || n.guid
    const name = n.name || n.Name || n.ousName || n.OUsName || n.text || n.Text || ''
    const children = n.children || n.Children || n.childFolder || n.ChildFolder || []
    result.push({ id, name, _level: level, _expanded: level < 1, _hasChildren: Array.isArray(children) && children.length > 0, _children: children })
    if (Array.isArray(children) && children.length > 0 && level < 1) {
      result.push(...flattenOrgTree(children, level + 1))
    }
  }
  return result
}

function toggleDeptNode(node) {
  node._expanded = !node._expanded
  // 重新展平
  deptTree.value = []
  // 简化：重新加载并展平
  // 实际应该用递归方式重新构建
  const raw = deptTree.value._rawData || []
  deptTree.value = flattenOrgTree(raw)
}

function confirmDeptSelect() {
  const node = deptTree.value.find(n => n.id === deptSelectedId.value)
  if (node) {
    processForm.value.department = node.id
  }
  closeDeptSelector()
}
function closeDeptSelector() { showDeptSelector.value = false }

// --- 摘要选择器 ---
const summaryActiveTab = ref(0)
const summaryTabs = ['当前步要拥有人', '代理人', '流程发起人', '日期/星期', '表单字段', '系统变量']
const summaryLoginUserFields = ['Account','Birthday','CostCenter','DateHired','Description','DisplayName','Email','HomePhone','HRID','Mobile','Office','OfficePhone','Sex.ToString()','WWWHomePage','LeaderTitle','Department']
const summaryAgentFields = ['Account','Birthday','CostCenter','DateHired','Description','DisplayName','Email','HomePhone','HRID','Mobile','Office','OfficePhone','Sex.ToString()','WWWHomePage','LeaderTitle','Department']
const summaryInitiatorFields = ['Account','Birthday','CostCenter','JoinDate','Description','DisplayName','Email','FaminlyTel','HRID','Mb','Office','Tel','Sex','Job','Levels','QQ']
const summaryDateFields = [
  { label: '今天:select convert(varchar(10),getdate(),120)', value: 'select convert(varchar(10),getdate(),120)' },
  { label: '昨天:select convert(varchar(10),getdate()-1,120)', value: 'select convert(varchar(10),getdate()-1,120)' },
  { label: '前天:select convert(varchar(10),getdate()-2,120)', value: 'select convert(varchar(10),getdate()-2,120)' },
  { label: '明天:select convert(varchar(10),getdate()+1,120)', value: 'select convert(varchar(10),getdate()+1,120)' },
  { label: '后天:select convert(varchar(10),getdate()+2,120)', value: 'select convert(varchar(10),getdate()+2,120)' }
]
const summaryFormFields = ref([])
const summarySysVars = ['TaskId', 'FlowGuid', 'FlowName']

function selectSummary() {
  summaryActiveTab.value = 0
  summaryFormFields.value = []
  if (editingProcessGuid.value) {
    loadSummaryFormFields()
  }
  showSummarySelector.value = true
}
async function loadSummaryFormFields() {
  try {
    const tables = await getDbTables(editingProcessGuid.value)
    const data = tables?.Data || tables?.data || []
    if (Array.isArray(data) && data.length > 0) {
      const tableName = data[0].tableName || data[0].TableName
      const cols = await getTableColumns(tableName)
      const colData = cols?.Data || cols?.data || []
      summaryFormFields.value = Array.isArray(colData) ? colData.map(c => c.columnName || c.ColumnName) : []
    }
  } catch (e) { /* ignore */ }
}
function onSummaryAppend(value) {
  const current = processForm.value.summary || ''
  processForm.value.summary = current ? current + ',' + value : value
}
function closeSummarySelector() { showSummarySelector.value = false }

// --- 图标选择器 ---
const iconTarget = ref('')
const iconList = ref([])
const iconLoading = ref(false)
const iconPage = ref(1)
const iconPageSize = 15
const iconTotal = ref(0)
const selectedIconName = ref('')

async function loadIcons(page = 1) {
  iconLoading.value = true
  iconPage.value = page
  try {
    const res = await getIcons(page, iconPageSize)
    const data = res?.Data || res?.data || []
    iconTotal.value = res?.Total || res?.total || 0
    iconList.value = Array.isArray(data) ? data : []
  } catch (e) {
    console.error('加载图标失败', e)
    iconList.value = []
  } finally {
    iconLoading.value = false
  }
}

function selectMenuIcon() { iconTarget.value = 'menuIcon'; selectedIconName.value = processForm.value.menuIcon || ''; loadIcons(1); showIconSelector.value = true }
function selectMenuIcon2() { iconTarget.value = 'menuIcon2'; selectedIconName.value = processForm.value.menuIcon2 || ''; loadIcons(1); showIconSelector.value = true }
function confirmIconSelect() {
  processForm.value[iconTarget.value] = selectedIconName.value
  closeIconSelector()
}
function closeIconSelector() { showIconSelector.value = false }
// ===== 数据库设计弹窗 =====
async function loadDbTables() {
  loadingDb.value = true
  try {
    const res = await getDbTables(editingDbFlowGuid.value)
    const data = res.Data || res.data || res
    dbTables.value = Array.isArray(data) ? data : []
  } catch (e) {
    console.error('加载数据表失败', e)
  } finally {
    loadingDb.value = false
  }
}
function closeDbModal() { showDbModal.value = false }

async function handleRemoveDbTable(item) {
  if (!await $confirm('确定移除此表绑定？')) return
  try {
    await deleteDbTable(item.guid || item.Guid)
    await loadDbTables()
  } catch (e) { $message.error('移除失败: ' + (e.message || e)) }
}

// --- 创建表 ---
function openCreateTableDialog() {
  createTableForm.value = {
    tableName: '', tableType: 0, tableRemark: '',
    columns: [{ columnName: 'ID', columnType: 'int', columnLength: '', columnDecimal: '', columnAllowEmpty: false, columnRemark: '主键ID', isKey: true }]
  }
  batchCol.value = { prefix: 'TA', digits: 3, startNo: 1, count: 10 }
  showCreateTableModal.value = true
}
function closeCreateTableModal() { showCreateTableModal.value = false }
function onTableNameInput() { /* placeholder */ }
function addColumn() {
  createTableForm.value.columns.push({ columnName: '', columnType: 'nvarchar', columnLength: '50', columnDecimal: '', columnAllowEmpty: false, columnRemark: '', isKey: false })
}
function removeColumn(idx) { createTableForm.value.columns.splice(idx, 1) }
function onKeyChange(idx) {
  if (createTableForm.value.columns[idx].isKey) {
    createTableForm.value.columns[idx].columnAllowEmpty = false
  }
}
function onColumnTypeChange(idx) {
  const col = createTableForm.value.columns[idx]
  if (!needLength(col.columnType)) { col.columnLength = ''; col.columnDecimal = '' }
  else if (!col.columnLength) {
    if (['char','nchar'].includes(col.columnType)) col.columnLength = '10'
    else if (['nvarchar','varchar'].includes(col.columnType)) col.columnLength = '50'
    else if (['decimal','numeric'].includes(col.columnType)) { col.columnLength = '18'; col.columnDecimal = '0' }
    else col.columnLength = ''
  }
}
function needLength(type) { return !['bigint','datetime','datetime2','smalldatetime','date','time','float','real','int','smallint','tinyint','bit','money','smallmoney','text','ntext','image','uniqueidentifier','xml'].includes(type) }
function needDecimal(type) { return ['decimal','numeric'].includes(type) }
function lengthPlaceholder(type) {
  if (['char','nchar','varchar','nvarchar','varbinary'].includes(type)) return '如 50 / MAX'
  if (['decimal','numeric'].includes(type)) return '精度'
  return '长度'
}
function batchGenerateColumns() {
  const { prefix, digits, startNo, count } = batchCol.value
  const pad = n => String(n).padStart(digits, '0')
  for (let i = 0; i < count; i++) {
    const seq = startNo + i
    const colName = prefix.toUpperCase() + pad(seq)
    // 检查是否已存在同名列，存在则跳过
    const exists = createTableForm.value.columns.some(c => c.columnName.toUpperCase() === colName)
    if (!exists) {
      createTableForm.value.columns.push({ columnName: colName, columnType: 'nvarchar', columnLength: '50', columnDecimal: '', columnAllowEmpty: true, columnRemark: '', isKey: false })
    }
  }
}

const createTableSql = computed(() => {
  const f = createTableForm.value
  if (!f.tableName) return ''
  let cols = []
  const keys = []
  f.columns.forEach(c => {
    if (!c.columnName) return
    let def = `[${c.columnName}] ${c.columnType}`
    if (needLength(c.columnType)) {
      // 支持 MAX 等自定义值
      const len = (c.columnLength || '').toString().toUpperCase()
      if (len === 'MAX') {
        def += '(MAX)'
      } else if (len && needDecimal(c.columnType)) {
        def += '(' + len + (c.columnDecimal ? ',' + c.columnDecimal : ',0') + ')'
      } else if (len) {
        def += '(' + len + ')'
      }
    }
    if (!c.columnAllowEmpty) def += ' NOT NULL'
    else def += ' NULL'
    cols.push(def)
    if (c.isKey) keys.push(c.columnName)
  })
  let sql = `CREATE TABLE [${f.tableName}] (\n  ` + cols.join(',\n  ')
  if (keys.length) sql += ',\n  PRIMARY KEY (' + keys.map(k => '[' + k + ']').join(', ') + ')'
  sql += '\n)'
  return sql
})

async function doCreateTable() {
  if (!createTableForm.value.tableName) { $message.warning('请输入表名'); return }
  const validCols = createTableForm.value.columns.filter(c => !!c.columnName)
  if (validCols.length === 0) { $message.warning('请至少定义一个列'); return }
  const sql = createTableSql.value
  if (!sql) { $message.warning('无法生成建表语句，请检查列定义'); return }
  try {
    await createTable({ CreateSql: sql })
    $message.success('建表成功')
    // 自动绑定到流程
    await saveDbTable({
      Type: 'Add',
      CatalogGuid: editingDbFlowGuid.value,
      TableName: createTableForm.value.tableName,
      TableType: createTableForm.value.tableType,
      TableRemark: createTableForm.value.tableRemark,
      DataSources: '',
      AssociateType: 0,
      SubmitCondition: 1,
      Active: 1,
      Para0: ''
    })
    closeCreateTableModal()
    await loadDbTables()
  } catch (e) { $message.error('建表失败: ' + (e.message || e)) }
}

// --- 新增现有表 ---
const filteredDbTables = computed(() => {
  if (!selSearch.value) return allDbTables.value
  const s = selSearch.value.toLowerCase()
  return allDbTables.value.filter(t =>
    t.tableName.toLowerCase().includes(s) ||
    (t.description || '').toLowerCase().includes(s)
  )
})

async function openSelTableDialog() {
  selTableForm.value = { tableName: '', tableType: 0, tableRemark: '' }
  selSearch.value = ''
  try {
    const res = await getDatabaseTables()
    const data = res.Data || res.data || res
    allDbTables.value = Array.isArray(data) ? data : []
  } catch (e) { console.error('获取表列表失败', e) }
  showSelTableModal.value = true
}
function closeSelTableModal() { showSelTableModal.value = false }

async function doAddExistingTable() {
  if (!selTableForm.value.tableName) { $message.warning('请选择表'); return }
  try {
    await saveDbTable({
      Type: 'Add',
      CatalogGuid: editingDbFlowGuid.value,
      TableName: selTableForm.value.tableName,
      TableType: selTableForm.value.tableType,
      TableRemark: selTableForm.value.tableRemark,
      DataSources: '',
      AssociateType: 0,
      SubmitCondition: 1,
      Active: 1,
      Para0: ''
    })
    closeSelTableModal()
    await loadDbTables()
  } catch (e) { $message.error('添加失败: ' + (e.message || e)) }
}

const DirRow = defineComponent({
  name: 'DirRow',
  props: { node: Object, depth: Number, selectedId: Number, expandedIds: Array },
  emits: ['select', 'edit', 'delete', 'toggleExpand'],
  setup(props, { emit }) {
    const hasChildren = computed(() => props.node.children && props.node.children.length > 0)
    return () => [
      h('tr', { class: { 'dir-row': true, selected: props.selectedId === props.node.id }, onClick: () => emit('select', props.node) }, [
        h('td', { style: { paddingLeft: (20 + props.depth * 20) + 'px' } }, [
          h('span', { class: 'dir-name-cell' }, [
            hasChildren.value
              ? h('span', { class: { 'expand-icon': true, expanded: props.expandedIds.includes(props.node.id) }, onClick: (e) => { e.stopPropagation(); emit('toggleExpand', props.node.id) } }, [h('i', { class: 'fas fa-caret-right' })])
              : h('span', { class: 'expand-placeholder' }),
            h('i', { class: 'fas ' + (hasChildren.value ? 'fa-folder' : 'fa-folder-open') }),
            h('span', { class: 'dir-name' }, props.node.name)
          ])
        ]),
        h('td', { class: 'action-cell' }, [
          h('button', { class: 'action-btn edit', title: '编辑', onClick: (e) => { e.stopPropagation(); emit('edit', props.node) } }, [h('i', { class: 'fas fa-edit' })]),
          h('button', { class: 'action-btn delete', title: '删除', onClick: (e) => { e.stopPropagation(); emit('delete', props.node) } }, [h('i', { class: 'fas fa-trash' })])
        ])
      ]),
      hasChildren.value && props.expandedIds.includes(props.node.id)
        ? props.node.children.map(child => h(DirRow, {
            key: child.id, node: child, depth: props.depth + 1,
            selectedId: props.selectedId, expandedIds: props.expandedIds,
            onSelect: (n) => emit('select', n), onEdit: (n) => emit('edit', n),
            onDelete: (n) => emit('delete', n), onToggleExpand: (id) => emit('toggleExpand', id)
          }))
        : null
    ]
  }
})
</script>

<style scoped>
.view-container { padding: 10px; background: #f5f7fa; }

.main-layout { display: flex; gap: 10px; min-height: calc(100vh - 120px); max-height: calc(100vh - 120px); }

/* ===== 左侧面板 ===== */
.left-panel { flex: 0 0 280px; background: #fff; border-radius: 4px; border: 1px solid #e4e7ed; overflow: hidden; display: flex; flex-direction: column; }
.panel-header { padding: 10px 12px; border-bottom: 1px solid #e4e7ed; display: flex; justify-content: space-between; align-items: center; background: #10b981; }
.panel-header h3 { margin: 0; font-size: 13px; font-weight: 600; color: #fff; }
.header-right { display: flex; gap: 6px; }
.btn-add-dir { padding: 4px 10px; border: 1px solid rgba(255,255,255,0.3); border-radius: 4px; background: rgba(255,255,255,0.1); color: #fff; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px; }
.btn-add-dir:hover { background: rgba(255,255,255,0.2); }
.panel-body { flex: 1; overflow-y: auto; padding: 5px 0; }

/* 目录表格 */
.dir-table { width: 100%; border-collapse: collapse; }
.dir-table thead th { padding: 6px 10px; text-align: left; font-size: 11px; font-weight: 600; color: #889099; background: #f8f9fa; border-bottom: 1px solid #e4e7ed; }
.dir-table tbody td { padding: 6px 10px; border-bottom: 1px solid #f2f6fc; font-size: 12px; color: #303133; }

.dir-row { cursor: pointer; transition: background 0.1s; }
.dir-row:hover { background: #f5f7fa; }
.dir-row.selected { background: #e8f5f0; }
.dir-row.root-row { border-bottom: 1px solid #e4e7ed; background: #fafbfc; }
.dir-row.root-row td { font-weight: 500; }
.dir-row.selected td { color: #10b981; }
.dir-name-cell { display: flex; align-items: center; gap: 4px; color: #303133; }
.dir-name-cell > i { font-size: 12px; color: #10b981; }
.dir-name { flex: 1; font-size: 12px; }
.expand-icon { width: 14px; height: 14px; display: flex; align-items: center; justify-content: center; color: #909399; font-size: 10px; cursor: pointer; }
.expand-icon:hover { color: #10b981; }
.expand-icon.expanded i { transform: rotate(90deg); }
.expand-placeholder { width: 14px; }
.dir-count { font-size: 10px; color: #909399; margin-left: auto; }
.action-cell { display: flex; gap: 4px; justify-content: flex-end; }
.action-btn { width: 22px; height: 22px; border: none; border-radius: 3px; background: #f5f7fa; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 11px; transition: all 0.15s; }
.action-btn.edit { color: #10b981; }
.action-btn.edit:hover { background: #10b981; color: #fff; }
.action-btn.delete { color: #f56c6c; }
.action-btn.delete:hover { background: #f56c6c; color: #fff; }
.empty-row { text-align: center; padding: 20px !important; color: #909399; }
.error-row { color: #f56c6c; }
.error-row span { display: block; margin-bottom: 6px; }
.btn-link { background: none; border: none; color: #10b981; cursor: pointer; font-size: 12px; text-decoration: underline; }
.btn-link:hover { color: #059669; }

/* ===== 右侧面板 ===== */
.right-panel { flex: 1; background: #fff; border-radius: 4px; border: 1px solid #e4e7ed; overflow: hidden; display: flex; flex-direction: column; }
.right-panel .panel-header { background: #10b981; }
.btn-add-process { padding: 4px 10px; border: 1px solid rgba(255,255,255,0.3); border-radius: 4px; background: rgba(255,255,255,0.1); color: #fff; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px; }
.btn-add-process:hover { background: rgba(255,255,255,0.2); }
.right-panel .panel-body { flex: 1; padding: 10px; overflow-y: auto; background: #f8f9fa; }

/* 流程卡片 */
.process-list { display: flex; flex-direction: column; gap: 10px; }
.process-card { background: #fff; border: 1px solid #e4e7ed; border-radius: 4px; overflow: hidden; transition: box-shadow 0.15s; }
.process-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.process-card-main { padding: 12px; display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.process-info { flex: 1.2; min-width: 0; }
.process-name-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.process-name { font-size: 14px; font-weight: 600; color: #1f2937; }
.process-version { font-size: 10px; color: #909399; background: #f5f7fa; padding: 1px 5px; border-radius: 3px; }
.valid-badge { font-size: 10px; padding: 1px 6px; border-radius: 10px; display: flex; align-items: center; gap: 3px; }
.valid-badge.valid { background: #e8f5f0; color: #10b981; }
.valid-badge.invalid { background: #fef3c7; color: #d97706; }
.process-meta { display: flex; gap: 12px; margin-bottom: 4px; }
.meta-item { font-size: 11px; color: #606266; display: flex; align-items: center; gap: 3px; }
.meta-item i { font-size: 10px; color: #909399; }
.process-desc { font-size: 11px; color: #909399; line-height: 1.4; }

/* 操作按钮组 */
.process-actions { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 320px; }
.action-group { display: flex; align-items: center; gap: 8px; }
.action-group-label { font-size: 10px; color: #909399; font-weight: 500; width: 30px; flex-shrink: 0; }
.action-group-btns { display: flex; gap: 3px; flex-wrap: wrap; flex: 1; }
.p-action-btn { padding: 4px 8px; border: none; border-radius: 3px; background: #f5f7fa; font-size: 11px; color: #606266; cursor: pointer; display: flex; align-items: center; gap: 3px; transition: all 0.15s; }
.p-action-btn i { font-size: 11px; }
.p-action-btn:hover { background: #e4e7ed; }
.p-action-btn.primary { background: #e8f5f0; color: #10b981; }
.p-action-btn.primary:hover { background: #10b981; color: #fff; }
.p-action-btn.warning { color: #e6a23c; }
.p-action-btn.warning:hover { background: #fef3c7; }
.p-action-btn.danger { color: #f56c6c; }
.p-action-btn.danger:hover { background: #fef0f0; }

/* 表单设计下拉菜单 */
.p-dropdown-btn { position: relative; display: inline-flex; }
.p-dropdown-arrow { margin-left: -1px; padding: 4px 5px; border: 1px solid transparent; border-left: 1px solid rgba(0,0,0,0.08); border-radius: 0 4px 4px 0; font-size: 9px; cursor: pointer; display: flex; align-items: center; line-height: 1; }
.p-dropdown-arrow.primary { background: #d1fae5; color: #10b981; }
.p-dropdown-arrow.primary:hover { background: #10b981; color: #fff; }
.p-dropdown-menu { position: absolute; top: 100%; left: 0; z-index: 50; min-width: 150px; background: #fff; border: 1px solid #d1fae5; border-radius: 6px; box-shadow: 0 4px 16px rgba(0,0,0,0.1); margin-top: 4px; padding: 4px; display: flex; flex-direction: row; white-space: nowrap; }
.p-dropdown-item { padding: 7px 12px; font-size: 12px; color: #374151; cursor: pointer; border-radius: 4px; display: flex; align-items: center; gap: 8px; white-space: nowrap; }
.p-dropdown-item i { width: 16px; text-align: center; color: #10b981; }
.p-dropdown-item:hover { background: #ecfdf5; color: #059669; }

/* 空状态 */
.no-processes { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 50px 20px; }
.no-processes p { font-size: 13px; color: #909399; margin-bottom: 10px; }
.btn-add-process-sm { padding: 5px 12px; border: 1px solid #10b981; border-radius: 4px; background: #fff; color: #10b981; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px; }
.btn-add-process-sm:hover { background: #e8f5f0; }
.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 20px; }
.empty-state i { font-size: 40px; color: #c0c4cc; margin-bottom: 12px; }
.empty-state p { font-size: 14px; color: #606266; margin: 3px 0; }
.empty-state .hint { font-size: 11px; color: #c0c4cc; }

/* ===== 弹窗 ===== */
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-dialog { width: 480px; max-height: 80vh; background: #fff; border-radius: 4px; box-shadow: 0 10px 30px rgba(0,0,0,0.15); display: flex; flex-direction: column; overflow: hidden; }
.modal-dialog.process-dialog { width: 700px; }
.modal-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 15px; border-bottom: 1px solid #e4e7ed; }
.modal-header h4 { font-size: 14px; font-weight: 600; color: #303133; margin: 0; }
.modal-close { background: none; border: none; font-size: 20px; color: #909399; cursor: pointer; padding: 0; line-height: 1; }
.modal-close:hover { color: #606266; }
.modal-body { padding: 15px; overflow-y: auto; flex: 1; }
.modal-footer { padding: 10px 15px; border-top: 1px solid #e4e7ed; display: flex; justify-content: flex-end; gap: 10px; }

/* 表单 */
.form-group { margin-bottom: 12px; }
.form-group label { display: block; font-size: 12px; color: #606266; margin-bottom: 4px; font-weight: 500; }
.form-group .checkbox-label { display: flex; align-items: center; gap: 5px; font-weight: 400; cursor: pointer; }
.form-control { width: 100%; padding: 7px 10px; border: 1px solid #dcdfe6; border-radius: 4px; font-size: 12px; outline: none; box-sizing: border-box; }
.form-control:focus { border-color: #10b981; }
textarea.form-control { resize: vertical; }
.form-row { display: flex; gap: 12px; margin-bottom: 12px; }
.form-row .form-group { flex: 1; margin-bottom: 0; }
.input-group { display: flex; gap: 6px; }
.input-group .form-control { flex: 1; }

/* 按钮 */
.btn { padding: 5px 12px; border-radius: 4px; font-size: 12px; cursor: pointer; border: 1px solid transparent; display: inline-flex; align-items: center; gap: 4px; transition: all 0.2s; }
.btn-default { background: #f5f7fa; border-color: #dcdfe6; color: #606266; }
.btn-default:hover { background: #e8eaed; }
.btn-primary { background: #10b981; border-color: #10b981; color: #fff; }
.btn-primary:hover { background: #059669; }

/* ===== 数据库设计弹窗 ===== */
.db-modal { width: 720px; }
.db-toolbar { display: flex; gap: 8px; margin-bottom: 12px; }
.db-modal .data-table { width: 100%; border-collapse: collapse; }
.db-modal .data-table thead th { padding: 6px 10px; font-size: 11px; color: #889099; background: #f8f9fa; border-bottom: 1px solid #e4e7ed; text-align: left; }
.db-modal .data-table tbody td { padding: 7px 10px; border-bottom: 1px solid #f2f6fc; font-size: 12px; color: #303133; }
.db-modal .data-table tbody tr:hover { background: #f5f7fa; }
.db-modal .empty-row { text-align: center; padding: 20px; color: #909399; }
.table-btn { padding: 3px 8px; border: none; border-radius: 3px; cursor: pointer; font-size: 11px; }
.table-btn.delete { background: #fef0f0; color: #f56c6c; }
.table-btn.delete:hover { background: #f56c6c; color: #fff; }

/* 创建表弹窗 */
.create-table-dialog { width: 880px; }
.batch-columns { background: #f0fdf6; border: 1px solid #bbf7d0; border-radius: 6px; padding: 10px 14px; margin-bottom: 12px; }
.batch-header { font-size: 12px; font-weight: 600; color: #166534; margin-bottom: 8px; display: flex; align-items: center; gap: 6px; }
.batch-header i { color: #10b981; }
.batch-hint { font-weight: 400; font-size: 11px; color: #6b7280; margin-left: 4px; }
.batch-row { display: flex; gap: 8px; align-items: flex-end; }
.batch-row .form-group { margin-bottom: 0; }
.batch-row .form-group label { font-size: 10px; color: #6b7280; margin-bottom: 3px; }
.w-full { width: 100%; }
.columns-section { margin-top: 8px; }
.columns-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; font-size: 12px; font-weight: 600; color: #303133; }
.columns-header i { color: #10b981; }
.columns-table-wrap { max-height: 300px; overflow-y: auto; border: 1px solid #e4e7ed; border-radius: 4px; }
.columns-table-wrap .data-table { margin: 0; }
.columns-table-wrap .data-table thead { position: sticky; top: 0; z-index: 2; }
.columns-table-wrap .data-table thead th { background: #f8f9fa; }
.columns-table-wrap .data-table td { padding: 4px 6px; vertical-align: middle; }
.columns-table-wrap .data-table tbody tr.key-row { background: #fffbeb; }
.columns-table-wrap .data-table tbody tr.key-row:hover { background: #fef3c7; }
.col-seq { text-align: center; font-size: 11px; color: #909399; font-weight: 500; }
.form-control-sm { padding: 4px 6px !important; font-size: 11px !important; height: 26px !important; }
.na-text { font-size: 11px; color: #c0c4cc; display: block; text-align: center; padding: 4px 0; }
.length-input-wrap { display: flex; align-items: center; gap: 3px; }
.length-input-wrap .form-control-sm { width: 60px; }
.decimal-label { font-size: 10px; color: #909399; white-space: nowrap; }
.decimal-input { width: 36px !important; }
.btn-icon { width: 22px; height: 22px; border: none; border-radius: 3px; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 10px; transition: all 0.15s; }
.btn-icon.del { background: #fef0f0; color: #f56c6c; }
.btn-icon.del:hover { background: #f56c6c; color: #fff; }
.sql-preview { margin-top: 12px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px 12px; }
.sql-preview-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.sql-preview-header label { font-size: 12px; color: #374151; font-weight: 600; }
.sql-preview-header label i { color: #6b7280; margin-right: 4px; }
.sql-hint { font-size: 10px; color: #9ca3af; }
.sql-textarea { background: #1e293b !important; color: #e2e8f0 !important; font-family: 'Cascadia Code', Consolas, monospace !important; font-size: 11px !important; border: none !important; border-radius: 4px !important; resize: none; }

/* 新增现有表弹窗 */
.sel-table-dialog { width: 640px; }
.sel-search { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; background: #f8f9fa; border: 1px solid #e4e7ed; border-radius: 6px; padding: 6px 10px; }
.sel-search i { color: #909399; font-size: 12px; }
.sel-search .form-control { flex: 1; border: none; background: transparent; font-size: 12px; padding: 4px; }
.sel-search .form-control:focus { outline: none; box-shadow: none; }
.sel-count { font-size: 11px; color: #909399; white-space: nowrap; }
.sel-table-list { max-height: 280px; overflow-y: auto; border: 1px solid #e4e7ed; border-radius: 6px; }
.sel-table-item { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-bottom: 1px solid #f2f6fc; cursor: pointer; transition: background 0.1s; font-size: 12px; }
.sel-table-item:hover { background: #f5f7fa; }
.sel-table-item.selected { background: #ecfdf5; border-left: 3px solid #10b981; }
.sel-table-item:last-child { border-bottom: none; }
.sel-type-icon { width: 22px; text-align: center; font-size: 12px; }
.sel-type-icon .fa-table { color: #3b82f6; }
.sel-type-icon .fa-eye { color: #8b5cf6; }
.sel-name { font-weight: 500; color: #1f2937; min-width: 120px; }
.sel-badge { font-size: 10px; padding: 1px 6px; border-radius: 8px; font-weight: 500; }
.sel-badge.table { background: #dbeafe; color: #1d4ed8; }
.sel-badge.view { background: #ede9fe; color: #6d28d9; }
.sel-desc { font-size: 11px; color: #909399; margin-left: auto; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sel-empty { text-align: center; padding: 20px; color: #909399; font-size: 12px; }
.btn-sm { padding: 4px 10px; font-size: 11px; }

/* ===== 选择器弹窗样式 ===== */
.serial-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 16px; }
.serial-item { display: flex; flex-direction: column; align-items: center; padding: 12px 8px; border: 1px solid #e5e7eb; border-radius: 8px; cursor: pointer; transition: all 0.15s; gap: 6px; }
/* ===== 选择器通用样式 (流水号/摘要) ===== */
.selector-tabs { display: flex; border-bottom: 2px solid #e5e7eb; margin-bottom: 12px; }
.selector-tab { padding: 8px 14px; font-size: 13px; color: #6b7280; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px; transition: all 0.15s; white-space: nowrap; }
.selector-tab:hover { color: #10b981; }
.selector-tab.active { color: #10b981; border-bottom-color: #10b981; font-weight: 600; }
.selector-tab-content { min-height: 180px; max-height: 350px; overflow-y: auto; }

.selector-tree-list { display: flex; flex-direction: column; gap: 2px; }
.selector-tree-item { display: flex; align-items: center; padding: 7px 10px; border-radius: 6px; cursor: pointer; font-size: 13px; color: #374151; transition: all 0.1s; }
.selector-tree-item:hover { background: #f0fdf4; color: #10b981; }
.selector-empty { padding: 20px; text-align: center; color: #9ca3af; font-size: 13px; }

/* ===== 流水号自增表单 ===== */
.serial-sn-form { display: flex; flex-direction: column; gap: 10px; padding: 10px; }
.serial-sn-row { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #374151; }
.serial-sn-row label { width: 60px; font-weight: 500; }

/* ===== 部门选择器树 ===== */
.dept-tree-container { max-height: 350px; overflow-y: auto; padding: 4px; }
.dept-tree-row { display: flex; align-items: center; padding: 6px 8px; border-radius: 6px; cursor: pointer; font-size: 13px; color: #374151; transition: all 0.1s; }
.dept-tree-row:hover { background: #f0fdf4; }
.dept-selected { background: #ecfdf5; color: #10b981; font-weight: 600; }
.dept-tree-indent { display: inline-block; flex-shrink: 0; }
.dept-tree-arrow { width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; font-size: 10px; color: #9ca3af; cursor: pointer; flex-shrink: 0; transition: transform 0.15s; }
.dept-tree-arrow.expanded { transform: rotate(90deg); }
.dept-tree-arrow-placeholder { width: 16px; flex-shrink: 0; }
.dept-tree-icon { margin: 0 6px; color: #10b981; font-size: 12px; flex-shrink: 0; }
.dept-tree-label { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* ===== 摘要当前值 ===== */
.summary-current { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px 12px; font-size: 12px; display: flex; align-items: center; gap: 6px; margin-top: 12px; }
.summary-current code { font-family: 'Consolas', monospace; color: #10b981; font-weight: 500; word-break: break-all; }

/* ===== 图标选择器 (图片网格) ===== */
.icon-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; max-height: 350px; overflow-y: auto; padding: 4px; }
.icon-item { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 8px 6px; border: 2px solid #e5e7eb; border-radius: 8px; cursor: pointer; transition: all 0.15s; background: #fff; }
.icon-item:hover { border-color: #10b981; background: #f0fdf4; transform: translateY(-1px); box-shadow: 0 2px 6px rgba(16,185,129,0.15); }
.icon-item.active { border-color: #10b981; background: #ecfdf5; box-shadow: 0 2px 8px rgba(16,185,129,0.2); }
.icon-img { width: 40px; height: 40px; object-fit: contain; }
.icon-name { font-size: 10px; color: #9ca3af; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%; }
.icon-pagination { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 12px; padding-top: 10px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280; }
.btn-page { width: 28px; height: 28px; border: 1px solid #e5e7eb; border-radius: 4px; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #374151; font-size: 12px; }
.btn-page:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-page:not(:disabled):hover { border-color: #10b981; color: #10b981; }
.page-info { font-weight: 500; }
.page-total { color: #9ca3af; font-size: 11px; }
</style>